import { useState, useEffect, useRef } from 'react';
import { X, Tag, Plus, Check, Loader2, ChevronDown } from 'lucide-react';
import { Modal } from '@mui/material';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';

interface TagData {
    id: number;
    name: string;
    organization_id: number;
    client_id: number | null;
}

interface AddTagModalProps {
    open: boolean;
    onClose: () => void;
    assetId: number | null;
    assetName: string;
    onSuccess: () => void;
}

const AddTagModal: React.FC<AddTagModalProps> = ({
    open,
    onClose,
    assetId,
    assetName,
    onSuccess,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [availableTags, setAvailableTags] = useState<TagData[]>([]);
    const [selectedTags, setSelectedTags] = useState<TagData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const justClosedDropdownRef = useRef(false);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                if (isDropdownOpen) {
                    setIsDropdownOpen(false);
                    justClosedDropdownRef.current = true;
                    setTimeout(() => {
                        justClosedDropdownRef.current = false;
                    }, 100);
                }
            }
        };

        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen]);

    const handleModalClose = (_event: {}, reason?: string) => {
        if (reason === 'backdropClick' && (isDropdownOpen || justClosedDropdownRef.current)) {
            return;
        }
        onClose();
    };

    // Initial load
    useEffect(() => {
        if (open) {
            fetchTags();
            setSelectedTags([]);
            setSearchTerm('');
            setIsDropdownOpen(false);
        }
    }, [open, assetId]);

    const storedLevel = localStorage.getItem('level');
    const level = storedLevel ? JSON.parse(storedLevel) : null;

    const fetchTags = async (query: string = '') => {
        setIsLoading(true);
        try {
            const queryObj: any = {};
            if (level?.id) {
                queryObj.client_id = level.id;
            }
            if (query.trim()) {
                queryObj.name = { "$ilike": `%${query}%` };
            }

            const params = {
                query: JSON.stringify(queryObj),
                limit: 20
            };

            const response = await baseapi.get('/tags', { params });
            if (response.data.status) {
                setAvailableTags(response.data.data);
            }
        } catch (error: any) {
            console.error('Failed to fetch tags', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            if (open) fetchTags(searchTerm);
        }, 200);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const handleCreateTag = async () => {
        if (!searchTerm.trim()) return;
        setIsCreating(true);
        try {
            const tagPayload: any = {
                name: searchTerm.trim(),
                ...(level?.id && { client_id: level.id })
            };
            const response = await baseapi.post('/tags', tagPayload);
            if (response.data.status) {
                const newTag: TagData = {
                    id: response.data.data.id,
                    name: searchTerm.trim(),
                    organization_id: 0,
                    client_id: level?.id || null
                };
                showSuccess('Tag created');
                setSelectedTags(prev => [...prev, newTag]);
                setSearchTerm('');
                fetchTags();
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setIsCreating(false);
        }
    };

    const handleToggleTag = (tag: TagData) => {
        setSelectedTags(prev => {
            const isSelected = prev.find(t => t.id === tag.id);
            if (isSelected) return prev.filter(t => t.id !== tag.id);
            return [...prev, tag];
        });
    };

    const handleSave = async () => {
        if (!assetId) return;
        setIsSaving(true);
        try {
            const tagIds = selectedTags.map(t => t.id);
            const response = await baseapi.put(`/assets/${assetId}`, { tag_ids: tagIds });
            if (response.data.status) {
                showSuccess('Tags updated');
                onSuccess();
                onClose();
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setIsSaving(false);
        }
    };

    const isMatchFound = availableTags.some(t => t.name.toLowerCase() === searchTerm.toLowerCase());

    return (
        <Modal open={open} onClose={handleModalClose} className="flex items-center justify-center p-4">
            <div className="w-full max-w-sm bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-800 rounded-xl shadow-2xl text-gray-900 dark:text-white outline-none">
                {/* Header - More compact */}
                <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800/50 flex items-center justify-between bg-gray-50 dark:bg-gray-900/40 rounded-t-xl">
                    <div className="flex items-center gap-2.5">
                        <Tag className="w-4 h-4 text-orange-500" />
                        <div>
                            <span className="text-sm font-bold tracking-wide text-gray-900 dark:text-white">Manage Tags</span>
                            <p className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest leading-none mt-0.5">{assetName}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-white transition-colors">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Content - Compact layout */}
                <div className="p-5 space-y-4">
                    <div className="relative" ref={dropdownRef}>
                        {/* Compact Multiselect Area */}
                        <div
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className={`min-h-[40px] w-full bg-gray-50 dark:bg-gray-900 border ${isDropdownOpen ? 'border-orange-500/50' : 'border-gray-200 dark:border-gray-800'} rounded-lg p-1.5 flex flex-wrap gap-1.5 cursor-text transition-all items-center pr-8`}
                        >
                            {selectedTags.map(tag => (
                                <span
                                    key={tag.id}
                                    className="flex items-center gap-1.5 px-2 py-0.5 bg-orange-600/10 border border-orange-500/20 text-orange-600 dark:text-orange-400 text-[10px] font-bold rounded"
                                >
                                    {tag.name}
                                    <X
                                        className="w-3 h-3 cursor-pointer hover:text-orange-800 dark:hover:text-white"
                                        onClick={(e) => { e.stopPropagation(); handleToggleTag(tag); }}
                                    />
                                </span>
                            ))}

                            <input
                                type="text"
                                className="bg-transparent border-none outline-none text-[12px] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-600 flex-1 min-w-[60px] py-1 px-1"
                                placeholder={selectedTags.length === 0 ? "Add tags..." : ""}
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    if (!isDropdownOpen) setIsDropdownOpen(true);
                                }}
                                onClick={(e) => e.stopPropagation()}
                            />

                            <ChevronDown className={`absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-600 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                        </div>

                        {/* Lean Dropdown Menu */}
                        {isDropdownOpen && (
                            <div className="absolute top-full left-0 right-0 mt-1.5 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">
                                <div className="max-h-[200px] overflow-y-auto custom-scroll p-1">
                                    {availableTags.length === 0 && !searchTerm && !isLoading && (
                                        <p className="py-4 text-[11px] text-gray-500 dark:text-gray-400 text-center italic font-medium">No tags available</p>
                                    )}

                                    {availableTags.map(tag => {
                                        const isSelected = selectedTags.some(t => t.id === tag.id);
                                        return (
                                            <button
                                                key={tag.id}
                                                onClick={(e) => { e.stopPropagation(); handleToggleTag(tag); }}
                                                className={`w-full flex items-center justify-between px-3 py-2 rounded text-[12px] transition-all ${isSelected ? 'bg-orange-600/10 text-orange-600 dark:text-orange-400 font-bold' : 'text-gray-700 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                                                    }`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <Tag className={`w-3.5 h-3.5 ${isSelected ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400 dark:text-gray-600'}`} />
                                                    <span>{tag.name}</span>
                                                </div>
                                                {isSelected && <Check className="w-3.5 h-3.5 text-orange-500" strokeWidth={3} />}
                                            </button>
                                        );
                                    })}

                                    {searchTerm && !isMatchFound && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleCreateTag(); }}
                                            disabled={isCreating}
                                            className="w-full flex items-center gap-3 px-3 py-2.5 text-orange-600 dark:text-orange-400 bg-orange-500/5 hover:bg-orange-500/10 rounded mt-1 border border-dashed border-orange-500/20"
                                        >
                                            {isCreating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                                            <span className="text-[11px] font-bold">Create "{searchTerm}"</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium px-1">
                        Select multiple tags from the list or create new ones by typing.
                    </p>
                </div>

                {/* Footer - Compact */}
                <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800/50 flex items-center justify-between bg-gray-50 dark:bg-gray-900/40 rounded-b-xl">
                    <span className="text-[9px] font-bold text-gray-500 dark:text-gray-600 uppercase tracking-widest">
                        {selectedTags.length} SELECTED
                    </span>
                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            className="px-3 py-1.5 text-[10px] font-bold text-gray-500 hover:text-gray-900 dark:text-gray-500 dark:hover:text-white transition-all uppercase tracking-wider"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isSaving || selectedTags.length === 0}
                            className="px-5 py-1.5 text-[10px] font-bold text-white bg-orange-600 hover:bg-orange-700 rounded-lg shadow-lg shadow-orange-900/10 disabled:opacity-30 transition-all flex items-center gap-2 uppercase tracking-wider"
                        >
                            {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                            Update
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default AddTagModal;
